import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { assetUrlsQueryOptions } from '@/entities/asset';
import {
  fontUrlIds,
  FontSpecimenGrid,
  toFontSpecimen,
  type BrandFontItem,
} from '@/entities/brand-font';

/**
 * The library's fonts as specimens, each drawn in its own typeface.
 *
 * Separate from the host so the font files are fetched only where they are shown. `FontRows` is the
 * list view's counterpart, drawing the same faces at thumbnail size.
 */
export const FontCards = ({
  fonts,
  selectedIds,
  openFontId,
  onChoose,
  onToggleSelect,
}: {
  /**
   * Memoise this in the host. A fresh array each render rebuilds the specimens below, and every
   * rebuild re-registers every `FontFace` with the document.
   */
  fonts: readonly BrandFontItem[];
  /** Bare font ids, not the library's qualified keys: the grid below knows only about fonts. */
  selectedIds: ReadonlySet<string>;
  openFontId?: string;
  onChoose: (fontId: string) => void;
  onToggleSelect: (fontId: string) => void;
}) => {
  const urls = useQuery(assetUrlsQueryOptions(fontUrlIds(fonts)));
  const specimens = useMemo(
    () => fonts.map((font) => toFontSpecimen(font, urls.data)),
    [fonts, urls.data],
  );

  return (
    <FontSpecimenGrid
      specimens={specimens}
      columns="fill"
      selectedIds={selectedIds}
      openFontId={openFontId}
      onChoose={onChoose}
      onToggleSelect={onToggleSelect}
    />
  );
};
